/**
 * /moniteur/shorts — Back-office pédagogique Shorts moniteur
 *
 * Permet au moniteur d'avoir une vue claire sur les Shorts proposés par
 * SES élèves (ceux encadrés via `linked_teacher_id`) et d'ajouter un
 * commentaire pédagogique + recommandation à l'admin avant modération finale.
 *
 * 3 colonnes visuelles : En attente ⏳ · Publiés ✅ · Refusés ❌
 * Actions rapides : 👍 Recommander · ⚠️ Déconseiller · 💬 Commenter
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Clapperboard, Clock, CheckCircle2, XCircle, Eye, Film,
  MessageSquare, ThumbsUp, ThumbsDown, Filter, Heart, Youtube, ExternalLink, AlertTriangle, X,
} from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

const THEME_LABELS = {
  equipement: 'Équipement', etm: 'ETM', plateau: 'Plateau', circulation: 'Circulation',
  trajectoires: 'Trajectoires', freinage: 'Freinage', 'angles-morts': 'Angles morts',
  interfiles: 'Interfiles', meteo: 'Météo', mentale: 'Préparation mentale',
  'gestes-signaleur': 'Gestes du signaleur', autre: 'Autre',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch { return '—'; }
};

const Thumb = ({ s }) => {
  if (s.youtube_id) {
    return <img src={`https://i.ytimg.com/vi/${s.youtube_id}/hqdefault.jpg`} alt="" loading="lazy" className="w-full h-full object-cover" />;
  }
  if (s.poster_url) return <img src={s.poster_url} alt="" loading="lazy" className="w-full h-full object-cover" />;
  return (
    <div className="w-full h-full bg-gradient-to-br from-indigo-900/40 to-slate-950/60 flex items-center justify-center">
      <Film className="w-8 h-8 text-white/50" />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    published: { label: 'Publié', icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    rejected: { label: 'Refusé', icon: XCircle, cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const RecommendationBadge = ({ rec }) => {
  if (!rec) return null;
  const map = {
    recommend: { label: 'Recommandé', cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40', icon: ThumbsUp },
    warn: { label: 'À vérifier', cls: 'bg-amber-500/20 text-amber-200 border-amber-500/40', icon: ThumbsDown },
  };
  const cfg = map[rec];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-widest border ${cfg.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {cfg.label}
    </span>
  );
};

const TeacherShortCard = ({ s, onFeedback }) => {
  const theme = THEME_LABELS[s.theme] || 'Autre';
  const external = s.source_url_original || s.embed_url || null;
  return (
    <article
      className="relative rounded-xl overflow-hidden border bg-[#0d0d0e] border-white/10 hover:border-white/25 transition"
      data-testid={`moniteur-short-${s.id}`}
    >
      <div className="aspect-video bg-black relative">
        <Thumb s={s} />
        <div className="absolute top-2 left-2 flex items-center gap-1 flex-wrap">
          <StatusBadge status={s.status} />
          <RecommendationBadge rec={s.teacher_recommendation} />
        </div>
        {s.status === 'published' && (
          <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
            <Eye className="w-3 h-3" /> {s.views ?? 0}
            {typeof s.likes === 'number' && (
              <>
                <span className="mx-0.5 text-white/40">·</span>
                <Heart className="w-3 h-3 text-red-400" /> {s.likes}
              </>
            )}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.14em] text-indigo-400 font-black flex-wrap">
          <span>{theme}</span>
          <span className="text-white/30">·</span>
          <span className="text-white/60 lowercase">
            <span className="normal-case font-bold">{s.author_name || 'Élève'}</span>
          </span>
          <span className="text-white/30">·</span>
          <span className="text-white/50">{formatDate(s.created_at)}</span>
        </div>
        <h3 className="text-[14px] font-black text-white leading-snug line-clamp-2">{s.title}</h3>
        {s.description && <p className="text-[12px] text-white/60 line-clamp-2">{s.description}</p>}

        {/* Mon commentaire précédent (si existe) */}
        {s.teacher_comment && (
          <div className="mt-2 rounded-lg border border-indigo-500/30 bg-indigo-950/30 p-2 text-[11.5px] text-indigo-200 leading-snug">
            <div className="font-bold mb-0.5 inline-flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> Mon retour
            </div>
            <div className="text-indigo-200/80">{s.teacher_comment}</div>
          </div>
        )}

        {s.status === 'rejected' && s.reject_reason && (
          <div className="mt-2 rounded-lg border border-red-500/30 bg-red-950/30 p-2 text-[11.5px] text-red-200 leading-snug">
            <div className="font-bold mb-0.5">Motif admin</div>
            <div className="text-red-200/80">{s.reject_reason}</div>
          </div>
        )}

        <div className="flex items-center gap-1.5 pt-1.5 flex-wrap">
          <button
            type="button"
            onClick={() => onFeedback(s, 'recommend')}
            className={`inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg text-[11px] font-black uppercase tracking-wider transition ${
              s.teacher_recommendation === 'recommend' ? 'bg-emerald-600 text-white' : 'bg-emerald-600/15 text-emerald-300 hover:bg-emerald-600/30 border border-emerald-500/30'
            }`}
            data-testid={`moniteur-short-recommend-${s.id}`}
            aria-label="Recommander à l'admin"
          >
            <ThumbsUp className="w-3 h-3" /> Recommander
          </button>
          <button
            type="button"
            onClick={() => onFeedback(s, 'warn')}
            className={`inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg text-[11px] font-black uppercase tracking-wider transition ${
              s.teacher_recommendation === 'warn' ? 'bg-amber-600 text-white' : 'bg-amber-600/15 text-amber-300 hover:bg-amber-600/30 border border-amber-500/30'
            }`}
            data-testid={`moniteur-short-warn-${s.id}`}
            aria-label="Signaler à l'admin"
          >
            <ThumbsDown className="w-3 h-3" /> Déconseiller
          </button>
          <button
            type="button"
            onClick={() => onFeedback(s, 'comment')}
            className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg bg-indigo-600/80 hover:bg-indigo-500 text-white text-[11px] font-black uppercase tracking-wider transition"
            data-testid={`moniteur-short-comment-${s.id}`}
          >
            <MessageSquare className="w-3 h-3" /> Commenter
          </button>
          {external && (
            <a
              href={external}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[11px] font-semibold transition"
            >
              {s.source_type === 'youtube' ? <Youtube className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
              Voir
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

const Section = ({ title, items, color, icon: Icon, emptyLabel, onFeedback }) => (
  <section className="space-y-3">
    <header className="flex items-center gap-2">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-white shadow" style={{ background: color }}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <h2 className="text-[13px] font-black uppercase tracking-[0.18em] text-white">
        {title} <span className="text-white/40 ml-1">({items.length})</span>
      </h2>
    </header>
    {items.length === 0 ? (
      <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center">
        <p className="text-white/50 text-[12.5px]">{emptyLabel}</p>
      </div>
    ) : (
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3">
        {items.map((s) => <TeacherShortCard key={s.id} s={s} onFeedback={onFeedback} />)}
      </div>
    )}
  </section>
);

export default function MoniteurShortsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shorts, setShorts] = useState([]);
  const [themeFilter, setThemeFilter] = useState('all');
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentShort, setCommentShort] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  useEffect(() => {
    document.title = 'Shorts élèves · Espace moniteur';
    (async () => {
      try {
        const data = await api.teacherShortsList('all');
        setShorts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.status === 401) return navigate('/login');
        if (e?.status === 403) {
          toast.error('Accès réservé aux moniteurs');
          return navigate('/');
        }
        toast.error('Impossible de charger les Shorts');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleFeedback = async (short, action) => {
    if (action === 'comment') {
      setCommentShort(short);
      setCommentText(short.teacher_comment || '');
      setCommentOpen(true);
      return;
    }
    // Quick toggle recommendation
    const newRec = short.teacher_recommendation === action ? 'none' : action;
    try {
      const r = await api.teacherShortsFeedback(short.id, { recommendation: newRec });
      setShorts((prev) => prev.map((x) => (x.id === short.id ? { ...x, ...r.short } : x)));
      toast.success(newRec === 'recommend' ? 'Short recommandé 👍' : newRec === 'warn' ? 'Short à vérifier ⚠️' : 'Avis retiré');
    } catch (e) {
      toast.error(e?.message || 'Action impossible');
    }
  };

  const saveComment = async () => {
    if (!commentShort) return;
    setCommentSaving(true);
    try {
      const r = await api.teacherShortsFeedback(commentShort.id, { comment: commentText.trim() });
      setShorts((prev) => prev.map((x) => (x.id === commentShort.id ? { ...x, ...r.short } : x)));
      toast.success('Retour envoyé à l\'élève');
      setCommentOpen(false);
    } catch (e) {
      toast.error(e?.message || 'Impossible d\'enregistrer');
    } finally {
      setCommentSaving(false);
    }
  };

  const filtered = useMemo(() => {
    if (themeFilter === 'all') return shorts;
    return shorts.filter((s) => s.theme === themeFilter);
  }, [shorts, themeFilter]);

  const { pending, published, rejected } = useMemo(() => ({
    pending: filtered.filter((s) => s.status === 'pending'),
    published: filtered.filter((s) => s.status === 'published'),
    rejected: filtered.filter((s) => s.status === 'rejected'),
  }), [filtered]);

  const availableThemes = useMemo(() => Array.from(new Set(shorts.map((s) => s.theme).filter(Boolean))), [shorts]);

  return (
    <div className="max-w-6xl space-y-8" data-testid="moniteur-shorts-page">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-indigo-400 font-black mb-1 inline-flex items-center gap-1.5">
            <Clapperboard className="w-3 h-3" /> Suivi pédagogique
          </div>
          <h1
            className="text-white text-[28px] sm:text-[36px] font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Shorts de mes élèves
          </h1>
          <p className="text-white/55 text-[13.5px] mt-1 max-w-xl leading-relaxed">
            Pré-validez les propositions de vos élèves et laissez-leur un retour pédagogique avant modération admin.
          </p>
        </div>
      </header>

      <section className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4" data-testid="moniteur-stat-pending">
          <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300 font-bold">À examiner</div>
          <div className="text-white text-3xl font-black mt-1">{pending.length}</div>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid="moniteur-stat-published">
          <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-bold">Publiés</div>
          <div className="text-white text-3xl font-black mt-1">{published.length}</div>
        </div>
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4" data-testid="moniteur-stat-rejected">
          <div className="text-[10px] uppercase tracking-[0.16em] text-red-300 font-bold">Refusés</div>
          <div className="text-white text-3xl font-black mt-1">{rejected.length}</div>
        </div>
      </section>

      {availableThemes.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap -mb-4" data-testid="moniteur-shorts-filter">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-white/50">
            <Filter className="w-3 h-3" /> Thème :
          </span>
          <button
            type="button"
            onClick={() => setThemeFilter('all')}
            className={`px-2.5 h-7 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${themeFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'}`}
          >
            Tous
          </button>
          {availableThemes.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setThemeFilter(t)}
              className={`px-2.5 h-7 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${themeFilter === t ? 'bg-indigo-600 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'}`}
            >
              {THEME_LABELS[t] || t}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/50">
          Chargement des Shorts élèves…
        </div>
      ) : shorts.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-950/30 to-black p-10 text-center space-y-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600/20 text-indigo-400 mx-auto">
            <Clapperboard className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-white text-[20px] font-black">Aucun Short d'élève pour l'instant</h2>
            <p className="text-white/55 text-[13px] mt-1 max-w-lg mx-auto">
              Dès qu'un de vos élèves rattachés proposera un Short, il apparaîtra ici pour pré-validation.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-10">
          <Section title="À examiner" items={pending} color="#d97706" icon={Clock} onFeedback={handleFeedback} emptyLabel="Aucun Short en attente de votre retour." />
          <Section title="Publiés" items={published} color="#059669" icon={CheckCircle2} onFeedback={handleFeedback} emptyLabel="Vos retours sur les Shorts publiés apparaîtront ici." />
          <Section title="Refusés par l'admin" items={rejected} color="#dc2626" icon={XCircle} onFeedback={handleFeedback} emptyLabel="Aucun Short refusé." />
        </div>
      )}

      <Dialog open={commentOpen} onOpenChange={setCommentOpen}>
        <DialogContent data-testid="moniteur-comment-dialog">
          <DialogHeader>
            <DialogTitle>Retour pédagogique</DialogTitle>
            <DialogDescription>
              Laissez un message qui sera visible par l'élève sur sa page « Mes Shorts » et par l'admin lors de la modération.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {commentShort && (
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5 text-[12px] text-white/70 mb-3">
                <span className="text-indigo-400 font-bold">{commentShort.author_name || 'Élève'}</span> · « {commentShort.title} »
              </div>
            )}
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Ton geste technique est bon, pense à bien regarder vers où tu veux aller…"
              maxLength={500}
              rows={5}
              data-testid="moniteur-comment-input"
            />
            <div className="text-[10px] text-white/40 mt-1">{commentText.length}/500</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCommentOpen(false)} disabled={commentSaving}>Annuler</Button>
            <Button onClick={saveComment} disabled={commentSaving} data-testid="moniteur-comment-save">
              <MessageSquare className="w-4 h-4 mr-1.5" />
              {commentSaving ? 'Envoi…' : 'Envoyer à l\'élève'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
